import React, { useEffect, useRef, useState } from "react";
import { CameraIcon } from "/src/helpers/components/upsellIcons.jsx";

const Chevron = ({ dir }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {dir === "left" ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
    </svg>
);

/**
 * Swipeable image gallery: native scroll-snap (so it swipes on touch devices with momentum and
 * no JS gesture code), prev/next buttons and dots overlaid on the image on larger screens.
 * `children` render on top of the images (tags, badges). The wrapper sizes the gallery — pass the
 * aspect/height classes in `className`.
 */
const ImageGallery = ({ images, alt = "", className = "", imgClassName = "", lazy = true, children }) => {
    const list = (images || []).filter(Boolean);
    const trackRef = useRef(null);
    const [index, setIndex] = useState(0);

    // Keep the dots in step with wherever the customer has swiped to.
    useEffect(() => {
        const el = trackRef.current;
        if (!el || list.length < 2) return undefined;
        let raf = 0;
        const onScroll = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => setIndex(Math.round(el.scrollLeft / el.clientWidth)));
        };
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => { el.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
    }, [list.length]);

    const goTo = (i, e) => {
        e?.stopPropagation();
        const el = trackRef.current;
        if (!el) return;
        const next = (i + list.length) % list.length;
        el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
        setIndex(next);
    };

    return (
        <div className={`relative overflow-hidden bg-[#0e2533] ${className}`}>
            {list.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center text-white/35
                                bg-[linear-gradient(135deg,#0e7fc7_0%,#08355c_100%)]">
                    <CameraIcon size={40} />
                </div>
            ) : (
                <div ref={trackRef}
                     className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                     style={{ overscrollBehaviorX: "contain", WebkitOverflowScrolling: "touch" }}>
                    {list.map((src, i) => (
                        <img key={src} src={src} alt={i === 0 ? alt : ""} loading={lazy && i > 0 ? "lazy" : undefined}
                             draggable={false}
                             className={`w-full h-full shrink-0 snap-center object-cover block select-none ${imgClassName}`} />
                    ))}
                </div>
            )}

            {list.length > 1 && (
                <>
                    <button type="button" onClick={(e) => goTo(index - 1, e)} aria-label="Previous photo"
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 text-primary shadow
                                       flex items-center justify-center cursor-pointer hover:bg-white">
                        <Chevron dir="left" />
                    </button>
                    <button type="button" onClick={(e) => goTo(index + 1, e)} aria-label="Next photo"
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 text-primary shadow
                                       flex items-center justify-center cursor-pointer hover:bg-white">
                        <Chevron dir="right" />
                    </button>
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
                        {list.map((_, i) => (
                            <span key={i} className={`h-1.5 rounded-full transition-all
                                ${i === index ? "w-4 bg-white" : "w-1.5 bg-white/55"}`} />
                        ))}
                    </div>
                </>
            )}
            {children}
        </div>
    );
};

export default ImageGallery;
